import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import Select from 'react-select';
import CustomSelectInput from './CustomSelectInput';
import { Button, CustomInput, Label, Row } from 'reactstrap';
import { Colxx } from './CustomBootstrap';

const DatepickerInput = ({ ...props }) => (
    <input type="text" {...props} readOnly />
);

const daysOfWeek = [
    { value: 'Sunday', label: 'Sunday' },
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' }
];

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
            const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            const displayTime = moment(timeString, 'HH:mm').format('hh:mm A');
            options.push({ value: timeString, label: displayTime });
        }
    }
    return options;
};

const timeOptions = generateTimeOptions();

const BannerSchedule = ({ value, onChange, selected = 'banners' }) => {
    const scheduleData = value || {
        scheduleType: 'normal',
        startDate: null,
        expiryDate: null,
        repeatSchedule: null
    };

    const [scheduleType, setScheduleType] = useState(scheduleData.scheduleType || 'normal');
    const [startDate, setStartDate] = useState(scheduleData.startDate ? moment(scheduleData.startDate).toDate() : null);
    const [expiryDate, setExpiryDate] = useState(scheduleData.expiryDate ? moment(scheduleData.expiryDate).toDate() : null);
    const [repeatSchedule, setRepeatSchedule] = useState(scheduleData.repeatSchedule || {
        days: [],
        startTime: null,
        endTime: null,
        is24Hours: false
    });

    // Sync with value prop when it changes externally
    useEffect(() => {
        if (value) {
            if (value.scheduleType && value.scheduleType !== scheduleType) {
                setScheduleType(value.scheduleType);
            }
            if (value.startDate !== undefined) {
                const newStartDate = value.startDate ? moment(value.startDate).toDate() : null;
                if (newStartDate?.getTime() !== startDate?.getTime()) {
                    setStartDate(newStartDate);
                }
            }
            if (value.expiryDate !== undefined) {
                const newExpiryDate = value.expiryDate ? moment(value.expiryDate).toDate() : null;
                if (newExpiryDate?.getTime() !== expiryDate?.getTime()) {
                    setExpiryDate(newExpiryDate);
                }
            }
            if (value.repeatSchedule && JSON.stringify(value.repeatSchedule) !== JSON.stringify(repeatSchedule)) {
                setRepeatSchedule(value.repeatSchedule);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Update parent when local state changes
    useEffect(() => {
        const updatedSchedule = {
            scheduleType,
            startDate: startDate ? moment(startDate).startOf('day').valueOf() : null,
            expiryDate: expiryDate ? moment(expiryDate).endOf('day').valueOf() : null,
            repeatSchedule: scheduleType === 'repeat' ? repeatSchedule : null
        };
        onChange(updatedSchedule);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleType, startDate, expiryDate, repeatSchedule]);

    const handleScheduleTypeChange = (newType) => {
        setScheduleType(newType);
        if (newType === 'normal') {
            // Clear repeat schedule when switching to normal
            setRepeatSchedule({
                days: [],
                startTime: null,
                endTime: null,
                is24Hours: false
            });
        } else {
            // Clear normal schedule when switching to repeat
            setStartDate(null);
            setExpiryDate(null);
        }
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleExpiryDateChange = (date) => {
        setExpiryDate(date);
    };

    const handleDayToggle = (day) => {
        const currentDays = repeatSchedule.days || [];
        const newDays = currentDays.includes(day)
            ? currentDays.filter(d => d !== day)
            : [...currentDays, day];
        setRepeatSchedule({ ...repeatSchedule, days: newDays });
    };

    const handle24HoursToggle = (checked) => {
        setRepeatSchedule({
            ...repeatSchedule,
            is24Hours: checked,
            startTime: checked ? null : repeatSchedule.startTime,
            endTime: checked ? null : repeatSchedule.endTime
        });
    };

    const handleStartTimeChange = (selectedOption) => {
        setRepeatSchedule({
            ...repeatSchedule,
            startTime: selectedOption ? selectedOption.value : null
        });
    };

    const handleEndTimeChange = (selectedOption) => {
        setRepeatSchedule({
            ...repeatSchedule,
            endTime: selectedOption ? selectedOption.value : null
        });
    };

    return (
        <div>
            <Row>
                <Colxx xxs="12" className="mb-3">
                    <Label>Schedule Type *</Label>
                    <Select
                        components={{ Input: CustomSelectInput }}
                        className="react-select"
                        value={{ value: scheduleType, label: scheduleType === 'normal' ? 'Normal' : 'Repeat' }}
                        options={[
                            { value: 'normal', label: 'Normal' },
                            { value: 'repeat', label: 'Repeat' }
                        ]}
                        onChange={(option) => handleScheduleTypeChange(option.value)}
                    />
                </Colxx>
            </Row>

            {scheduleType === 'normal' && (
                <>
                    <Row>
                        <Colxx xxs="12" md="6" className="mb-3">
                            <Label>Start Date (Optional)</Label>
                            <DatePicker
                                popperClassName="redatepicker"
                                customInput={<DatepickerInput />}
                                selected={startDate}
                                onChange={handleStartDateChange}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Select start date"
                                isClearable
                            />
                        </Colxx>
                        <Colxx xxs="12" md="6" className="mb-3">
                            <Label>Expiration Date {startDate ? '*' : ''}</Label>
                            <DatePicker
                                popperClassName="redatepicker"
                                customInput={<DatepickerInput />}
                                selected={expiryDate}
                                onChange={handleExpiryDateChange}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Select expiration date"
                                minDate={startDate}
                                isClearable
                            />
                        </Colxx>
                    </Row>
                </>
            )}

            {scheduleType === 'repeat' && (
                <>
                    <Row>
                        <Colxx xxs="12" className="mb-3">
                            <Label>Select Days *</Label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {daysOfWeek.map(day => {
                                    const isSelected = (repeatSchedule.days || []).includes(day.value);
                                    return (
                                        <Button
                                            key={day.value}
                                            outline={!isSelected}
                                            color={isSelected ? 'primary' : 'secondary'}
                                            size="sm"
                                            onClick={() => handleDayToggle(day.value)}
                                            style={{
                                                minWidth: '80px',
                                                borderColor: isSelected ? '#007bff' : '#6c757d',
                                                backgroundColor: isSelected ? '#007bff' : 'transparent',
                                                color: isSelected ? '#fff' : '#6c757d'
                                            }}
                                        >
                                            {day.label.substring(0, 3)}
                                        </Button>
                                    );
                                })}
                            </div>
                        </Colxx>
                    </Row>

                    <Row>
                        <Colxx xxs="12" className="mb-3">
                            <CustomInput
                                type="checkbox"
                                id="is24Hours"
                                checked={repeatSchedule.is24Hours}
                                onChange={(e) => handle24HoursToggle(e.target.checked)}
                                label="Display banner 24 hours (all day)"
                            />
                        </Colxx>
                    </Row>

                    {!repeatSchedule.is24Hours && (
                        <Row>
                            <Colxx xxs="12" md="6" className="mb-3">
                                <Label>Start Time (Optional)</Label>
                                <Select
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    value={repeatSchedule.startTime ? timeOptions.find(opt => opt.value === repeatSchedule.startTime) : null}
                                    options={timeOptions}
                                    onChange={handleStartTimeChange}
                                    placeholder="Select start time"
                                    isClearable
                                />
                            </Colxx>
                            <Colxx xxs="12" md="6" className="mb-3">
                                <Label>End Time (Optional)</Label>
                                <Select
                                    components={{ Input: CustomSelectInput }}
                                    className="react-select"
                                    value={repeatSchedule.endTime ? timeOptions.find(opt => opt.value === repeatSchedule.endTime) : null}
                                    options={timeOptions}
                                    onChange={handleEndTimeChange}
                                    placeholder="Select end time"
                                    isClearable
                                />
                            </Colxx>
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

export default BannerSchedule;
